<template>
  <div class="block-rounded block-mode-hidden block">
    <div id="map" ref="mapContainer" style="width: 100%; height: 500px;"></div>
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue'

const props = defineProps({
  points: {
    type: Array,
    required: true
  },
  selectedShipments: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['shipment-toggled'])

const mapContainer = ref(null)
let map = null
let shipmentMarkers = {}

// Create a local copy of selected shipments that stays in sync
const localSelected = ref([...props.selectedShipments])

// Watch for external changes to selectedShipments
watch(() => props.selectedShipments, (newVal) => {
  if (JSON.stringify(newVal) !== JSON.stringify(localSelected.value)) {
    localSelected.value = [...newVal]
    updateSelectedMarkers()
  }
}, { deep: true })

// Watch for points changes
watch(() => props.points, (newPoints) => {
  if (map) {
    clearMarkers()
    addPointsToMap(newPoints)
    updateSelectedMarkers()
  }
}, { deep: true })

function initMap() {
  if (map) map.remove()

  map = L.map(mapContainer.value).setView([30.0444, 31.2357], 10)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors'
  }).addTo(map)

  map.setMaxBounds([
    [22.0, 24.7],  // Southwest
    [31.7, 37.0]   // Northeast
  ])

  setTimeout(() => map.invalidateSize(), 500)
}

function clearMarkers() {
  Object.values(shipmentMarkers).forEach(marker => {
    if (marker && map.hasLayer(marker)) {
      map.removeLayer(marker)
    }
  })
  shipmentMarkers = {}
}

function addPointsToMap(points) {
  points.forEach(point => {
    if (!point.latitude || !point.longitude) return

    const isSelected = localSelected.value.includes(point._id)
    const marker = L.marker([point.latitude, point.longitude], {
      icon: L.icon({
        iconUrl: getMarkerIcon(point._id, point.is_dispatched, isSelected),
        shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
        iconSize: [35, 41],
        iconAnchor: [17, 41]
      }),
      zIndexOffset: isSelected ? 1000 : 0 // Bring selected markers to front
    }).addTo(map)

    shipmentMarkers[point._id] = marker

    marker.on('click', () => {
      toggleShipmentSelection(point._id)
    })

    marker.on('mouseover', () => {
      marker.bindTooltip(`${point.reference}`, { direction: 'top' }).openTooltip()
    })

    marker.on('mouseout', () => {
      marker.closeTooltip()
    })
  })
}

function toggleShipmentSelection(id) {
  const newSelection = [...localSelected.value]
  const index = newSelection.indexOf(id)

  if (index > -1) {
    newSelection.splice(index, 1)
  } else {
    newSelection.push(id)
  }

  localSelected.value = newSelection
  emit('shipment-toggled', newSelection)
  updateSelectedMarkers()
}

function updateSelectedMarkers() {
  props.points.forEach(point => {
    const marker = shipmentMarkers[point._id]
    if (marker) {
      const isSelected = localSelected.value.includes(point._id)
      marker.setIcon(L.icon({
        iconUrl: getMarkerIcon(point._id, point.is_dispatched, isSelected),
        shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
        iconSize: [35, 41],
        iconAnchor: [17, 41]
      }))
      marker.setZIndexOffset(isSelected ? 1000 : 0)
    }
  })
}

function getMarkerIcon(id, isDispatched, isSelected) {
  if (isSelected) {
    return '/media/various/package-red.png'
  } else if (isDispatched) {
    return '/media/various/package-yellow.png'
  } else {
    return '/media/various/package-blue.png'
  }
}

onMounted(() => {
  initMap()
  addPointsToMap(props.points)
  updateSelectedMarkers()
})

onBeforeUnmount(() => {
  if (map) map.remove()
})
</script>

<style scoped>
  #map {
    border: 1px solid #ccc;
    border-radius: 8px;
  }
</style>